import { useRef, useState } from 'react';
import { Box, Button, Group, Stack, Text, Title } from '@mantine/core';
import PageLoader from 'src/UI/loader/PageLoader';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import useMemories from './useMemories';
import useSaveMemories from './useSaveMemories';

type MemoriesEditorProps = {
  title: string;
  description: string;
  emptyText: string;
  queryKey: string[];
  apiEndpoint: string;
  canEdit?: boolean;
};

export default function MemoriesEditor({
  title,
  description,
  emptyText,
  queryKey,
  apiEndpoint,
  canEdit = true,
}: MemoriesEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const editValueRef = useRef('');

  const { data, isLoading, isError } = useMemories(queryKey);
  const { mutateAsync: saveMemories, isPending: isSaving } = useSaveMemories(queryKey, apiEndpoint);

  const handleEdit = () => {
    editValueRef.current = data?.markdown ?? '';
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      await saveMemories(editValueRef.current);
      setIsEditing(false);
    } catch (error) {
      notifyAndLogError(
        'Failed to save memories',
        'An error occurred while saving memories.',
        error,
      );
    }
  };

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return <Text c="red">Failed to load memories. Please try again later.</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={3}>{title}</Title>
        {canEdit && !isEditing && (
          <Button variant="light" size="xs" onClick={handleEdit}>
            Edit
          </Button>
        )}
      </Group>

      <Text size="sm" c="dimmed">
        {description}
      </Text>

      {isEditing ? (
        <Stack gap="sm">
          <MarkdownEditor
            defaultValue={editValueRef.current}
            onChange={value => {
              editValueRef.current = value;
            }}
            rootClass="compact"
          />
          <Group>
            <Button size="xs" onClick={handleSave} loading={isSaving}>
              Save
            </Button>
            <Button size="xs" variant="subtle" onClick={handleCancel}>
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Box>
          {data?.markdown ? (
            <MarkdownView value={data.markdown} />
          ) : (
            <Text c="dimmed" fs="italic">
              {emptyText}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
}
